import { GAME_ASSET_SLUG } from "@/config/game";
import { Dropdown, DropdownItem } from "@/design-system/Dropdown/Dropdown";
import {
  PlayerCardWithMetaRating,
  PlayerCardWithMetaRatingSkeleton,
} from "@/design-system/PlayerCard/PlayerCard";
import { cn } from "@/lib/utils";
import {
  FavoriteType,
  favoritesFlowSelector,
  fetchFavoriteClubPlayers,
  fetchFavoriteCountryPlayers,
  frontPageSelector,
  setSelectedCountry,
  toggleSelectedClub,
} from "@/redux/frontPage/frontPageSlice";
import {
  TOP_NINE_COUNTRY_IDS,
  loadedContentSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { Check, X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

const POPULAR_CLUB_IDS: number[] = [243, 241, 11, 73, 10, 21, 9, 5];

type Props = {
  type: FavoriteType;
};

/*
  One panel shared by both picker steps. Left column = searchable dropdown + a
  "popular" tile grid (multi-select for clubs, single-select for country). Right
  column = a live preview of that selection's top players.
*/
export const FavoritePickerPanel = ({ type }: Props) => {
  const { t } = useTranslation("frontPage");
  const dispatch = useDispatch<AppDispatch>();
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const { clubs, countries } = useSelector(loadedContentSelector);
  const { selectedClubIds, selectedCountryId } = useSelector(
    favoritesFlowSelector,
  );

  const isClub = type === "club";

  const dropdownItems: DropdownItem[] = useMemo(() => {
    if (isClub) {
      return (clubs ?? []).map((club) => ({
        value: String(club.id),
        label: club.isWomen
          ? t("favorites.picker.womensClub", { name: club.name })
          : club.name,
        icon: (
          <img
            src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${club.id}.png?scale=50`}
            alt={club.name}
            className="h-5 w-5"
          />
        ),
      }));
    }
    return (countries ?? []).map((country) => ({
      value: String(country.id),
      label: country.name,
      icon: (
        <img
          src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${country.id}.png?scale=50`}
          alt={country.name}
          className="w-5"
        />
      ),
    }));
  }, [isClub, clubs, countries, assetsBaseUrl, t]);

  const popularClubs = useMemo(
    () =>
      POPULAR_CLUB_IDS.map((id) =>
        (clubs ?? []).find((c) => c.id === id),
      ).filter((c): c is NonNullable<typeof c> => Boolean(c)),
    [clubs],
  );

  const selectedClubs = useMemo(
    () =>
      selectedClubIds
        .map((id) => (clubs ?? []).find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => Boolean(c)),
    [selectedClubIds, clubs],
  );

  const popularCountries = useMemo(
    () =>
      TOP_NINE_COUNTRY_IDS.map((id) =>
        (countries ?? []).find((c) => c.id === id),
      ).filter((c): c is NonNullable<typeof c> => Boolean(c)),
    [countries],
  );

  const onPickDropdown = (item: DropdownItem) => {
    const id = Number(item.value);
    if (isClub) {
      if (!selectedClubIds.includes(id)) dispatch(toggleSelectedClub(id));
    } else {
      dispatch(setSelectedCountry(id));
    }
  };

  return (
    <div className="flex flex-col gap-5 mobile:flex-row mobile:items-stretch min-h-120">
      {/* Left: dropdown + popular grid */}
      <div className="flex min-w-0 flex-1 flex-col gap-3.5">
        <div className="flex flex-col gap-2">
          <Dropdown
            items={dropdownItems}
            placeholder={t("favorites.picker.placeholder")}
            onChange={onPickDropdown}
          />

          {isClub && selectedClubs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedClubs.map((club) => (
                <span
                  key={club.id}
                  className="flex items-center gap-1.5 rounded-full border border-neutral-700 bg-neutral-800 py-1 pl-1.5 pr-2"
                >
                  <img
                    src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${club.id}.png?scale=50`}
                    alt={club.name}
                    className="size-5 object-contain"
                  />
                  <span className="text-xs text-neutral-100">{club.name}</span>
                  <button
                    type="button"
                    aria-label={t("favorites.picker.remove", {
                      name: club.name,
                    })}
                    onClick={() => dispatch(toggleSelectedClub(club.id))}
                    className="flex size-4 items-center justify-center rounded-full bg-neutral-600 text-neutral-200 hover:bg-neutral-500"
                  >
                    <X size={11} strokeWidth={2.5} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col rounded-2xl border border-neutral-800 bg-neutral-900/40 p-4">
          <div className="mb-3.5 flex items-baseline justify-between">
            <h3 className="text-sm font-medium uppercase tracking-wide text-neutral-200">
              {isClub
                ? t("favorites.picker.popularClubs")
                : t("favorites.picker.popularCountries")}
            </h3>
            <span className="text-[11px] text-neutral-500">
              {isClub
                ? t("favorites.picker.maxClubs", { count: 3 })
                : t("favorites.picker.chooseOne")}
            </span>
          </div>

          {isClub ? (
            <div className="flex flex-wrap gap-3">
              {popularClubs.map((club) => (
                <PickerTile
                  key={club.id}
                  selected={selectedClubIds.includes(club.id)}
                  onClick={() => dispatch(toggleSelectedClub(club.id))}
                >
                  <img
                    src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/dark/${club.id}.png`}
                    alt={club.name}
                    className="size-11 object-contain"
                  />
                </PickerTile>
              ))}
            </div>
          ) : (
            /* Compact single row — round flags hide low-res flag edges. */
            <div className="flex flex-wrap gap-x-3 gap-y-2">
              {popularCountries.map((country) => {
                const selected = selectedCountryId === country.id;
                return (
                  <button
                    key={country.id}
                    type="button"
                    onClick={() =>
                      dispatch(setSelectedCountry(selected ? null : country.id))
                    }
                    className="flex w-16 flex-col items-center gap-1.5"
                  >
                    <span
                      className={cn(
                        "relative flex size-11 items-center justify-center rounded-full border-2 transition-colors",
                        selected
                          ? "border-blue-500"
                          : "border-transparent hover:border-neutral-700",
                      )}
                    >
                      <img
                        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${country.id}.png`}
                        alt={country.name}
                        className="size-9 rounded-full object-cover"
                      />
                      {selected && (
                        <span className="absolute -right-1 -top-1 flex size-[15px] items-center justify-center rounded-full bg-blue-500 text-white">
                          <Check size={9} strokeWidth={3} />
                        </span>
                      )}
                    </span>
                    <span className="w-full truncate text-center text-[11px] text-neutral-400">
                      {country.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right: live preview */}
      <FavoritePreview type={type} />
    </div>
  );
};

type TileProps = {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

const PickerTile = ({ selected, onClick, children }: TileProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative flex h-[86px] w-[138px] items-center justify-center overflow-hidden rounded-xl border bg-neutral-900 transition-colors",
      selected
        ? "border-2 border-blue-500"
        : "border-neutral-800 hover:border-neutral-700",
    )}
  >
    {children}
    {selected && (
      <span className="absolute right-1.5 top-1.5 flex size-[18px] items-center justify-center rounded-full bg-blue-500 text-white">
        <Check size={11} strokeWidth={3} />
      </span>
    )}
  </button>
);

/** Live top-players preview for the currently highlighted club/country. */
const FavoritePreview = ({ type }: { type: FavoriteType }) => {
  const { t } = useTranslation("frontPage");
  const dispatch = useDispatch<AppDispatch>();
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const { clubs, countries } = useSelector(loadedContentSelector);
  const { selectedClubIds, selectedCountryId } = useSelector(
    favoritesFlowSelector,
  );
  const {
    favoriteClubPlayers,
    favoriteClubPlayersStatus,
    favoriteCountryPlayers,
    favoriteCountryPlayersStatus,
  } = useSelector(frontPageSelector);

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  let count = 3;
  if (isTouchDevice) count = 2;
  if (isMobile) count = 1;

  const isClub = type === "club";
  const activeId = isClub
    ? (selectedClubIds[selectedClubIds.length - 1] ?? null)
    : selectedCountryId;

  useEffect(() => {
    if (!activeId) return;
    if (isClub) {
      dispatch(
        fetchFavoriteClubPlayers({ clubId: activeId, activeTab: "players" }),
      );
    } else {
      dispatch(fetchFavoriteCountryPlayers({ countryId: activeId }));
    }
  }, [activeId, isClub, dispatch]);

  const entity = isClub
    ? (clubs ?? []).find((c) => c.id === activeId)
    : (countries ?? []).find((c) => c.id === activeId);
  const imgSrc = activeId
    ? isClub
      ? `${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${activeId}.png?scale=50`
      : `${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${activeId}.png?scale=50`
    : null;

  const players = isClub ? favoriteClubPlayers : favoriteCountryPlayers;
  const status = isClub
    ? favoriteClubPlayersStatus
    : favoriteCountryPlayersStatus;

  return (
    <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-neutral-800 bg-neutral-900/40 p-4">
      <div className="flex items-center gap-2.5 border-b border-neutral-800 pb-3">
        {imgSrc && (
          <img
            src={imgSrc}
            alt={entity?.name ?? ""}
            className="size-[30px] object-contain"
          />
        )}
        <span className="text-lg font-bold uppercase tracking-tight text-neutral-100">
          {entity?.name ??
            (isClub
              ? t("favorites.picker.selectClub")
              : t("favorites.picker.selectCountry"))}
        </span>
      </div>

      {/*
        Outer wrapper takes the panel's free height and centers; the inner row
        is content-height so the cards' internal `h-full` resolves to their own
        content instead of stretching to fill the tall panel.
      */}
      <div className="mt-4 flex flex-1 items-center justify-center">
        <div className="flex flex-wrap items-start justify-center gap-3.5">
          {!activeId ? (
            <span className="text-sm text-neutral-500">
              {isClub
                ? t("favorites.picker.previewHintClub")
                : t("favorites.picker.previewHintCountry")}
            </span>
          ) : status === "pending" ? (
            Array.from({ length: count }).map((_, i) => (
              <PlayerCardWithMetaRatingSkeleton key={i} scale={0.8} />
            ))
          ) : (
            players.slice(0, count).map((player) => (
              // A preview only: the cards can't be opened, so no hover.
              <PlayerCardWithMetaRating
                key={player.resourceId}
                scale={0.8}
                player={player}
                hoverable={false}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
